'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { FC, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';
import { boolean, z } from 'zod';

import { Box } from '@virginmediao2/momentum-nextjs/box';
import { Button } from '@virginmediao2/momentum-nextjs/button';
import {
  ErrorAlert,
  ErrorAlertsRef,
  FormCheckboxInput,
  FormInput,
  FormRadioGroup,
  FormRadioInput,
  FormTextarea,
} from '@virginmediao2/momentum-nextjs/forms';
import { Text } from '@virginmediao2/momentum-nextjs/text';

import { FormCheckboxGroup } from '@/components/momentum/components/forms/form-checkbox';
import { sendSupportRequest } from '@/lib/support-request';
import { extendedUKMobileNumberRegex } from '@/lib/utils/regex-validators';

import styles from './support-request-form.module.css';

const ACCESS_FOR_YOU_EMAIL = 'accessforyou@o2.com';

export enum AccountHolder {
  YES = 'yes',
  NO = 'no',
}

export enum RequestFor {
  ME = 'me',
  ANOTHER = 'another',
}

export enum ContactOptions {
  PHONE = 'phone',
  TEXT = 'text',
  EMAIL = 'email',
  LETTER = 'letter',
  OTHER = 'other',
}

export enum Nominate {
  YES = 'yes',
  NO = 'no',
}

export enum LpaRegister {
  YES = 'yes',
  NO = 'no',
}

const lpaRegex = /^\d{4}-\d{4}-\d{4}$/;

const supportRequestFormSchema = z
  .object({
    accountHolder: z
      .nativeEnum(AccountHolder)
      .refine(Boolean, 'Requires at least one option to be selected'),
    firstName: z.string().min(2, 'First name is required'),
    lastName: z.string().min(2, 'Last name is required'),
    mobileNumber: z
      .string()
      .regex(extendedUKMobileNumberRegex, 'Invalid mobile number'),
    email: z.string().email({ message: 'Invalid email address' }),
    requestFor: z
      .nativeEnum(RequestFor)
      .refine(Boolean, 'Requires at least one option to be selected'),
    contactPreference: z
      .union([z.nativeEnum(ContactOptions), z.literal(false)])
      .array()
      .refine(
        (values) => values.find(Boolean),
        'Checkbox requires at least one option must be selected'
      )
      .transform((val) => val.filter(Boolean)),
    contactPreferenceOtherDetail: z.string().optional(),
    nominate: z
      .nativeEnum(Nominate)
      .refine(Boolean, 'Requires at least one option to be selected'),
    nominee: z.string().optional(),
    lpaRegister: z
      .nativeEnum(LpaRegister)
      .optional()
      .nullable()
      .transform((val) => val ?? undefined),
    lpaCode: z.string().optional().or(z.literal('')),
    support: z.string().min(1, 'Support needs is required'),
    consent: boolean().refine(Boolean, 'Consent is required'),
  })
  .superRefine((data, ctx) => {
    if (data.accountHolder === AccountHolder.NO) {
      ctx.addIssue({
        code: 'custom',
        path: ['accountHolder'],
        message: `
          Only the O2 account holder can fill out our Support Request Form.
          If you're a third party supporting an O2 account holder, please contact
          our Access for You Team - accessforyou@o2.com with account holder details.
        `,
      });
    }

    if (data.nominate === 'yes' && !data.nominee?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['nominee'],
        message: 'Please specify your nominated friend or family member',
      });
    }

    if (
      data.contactPreference.includes(ContactOptions.OTHER) &&
      !data.contactPreferenceOtherDetail?.trim()
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['contactPreferenceOtherDetail'],
        message:
          'Please specify your contact preference if "Other" is selected',
      });
    }

    if (
      data.requestFor === RequestFor.ANOTHER &&
      data.lpaRegister === LpaRegister.YES
    ) {
      if (!data.lpaCode?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['lpaCode'],
          message: 'Please input the LPA code',
        });
      } else {
        if (!lpaRegex.test(data.lpaCode.trim())) {
          ctx.addIssue({
            code: 'custom',
            path: ['lpaCode'],
            message: 'Please provide the LPA code in the format 0000-0000-0000',
          });
        } else if (data.lpaCode.trim() === '0000-0000-0000') {
          ctx.addIssue({
            code: 'custom',
            path: ['lpaCode'],
            message: 'Please provide a valid LPA code',
          });
        }
      }
    }
  });

export type SupportRequestFormData = z.infer<typeof supportRequestFormSchema>;

const contactPreferenceInputList: Array<{
  name: ContactOptions;
  label: string;
}> = [
  {
    name: ContactOptions.PHONE,
    label: 'Phone',
  },
  {
    name: ContactOptions.TEXT,
    label: 'Text',
  },
  {
    name: ContactOptions.EMAIL,
    label: 'Email',
  },
  {
    name: ContactOptions.LETTER,
    label: 'Letter',
  },
  {
    name: ContactOptions.OTHER,
    label: 'Other, please state below',
  },
];

export interface SupportRequestFormProps {
  recaptchaSiteKey: string;
  errorMessage: string;
  successUrl: string;
  privacyPolicyUrl: string;
}

export const SupportRequestForm: FC<SupportRequestFormProps> = ({
  recaptchaSiteKey,
  errorMessage,
  successUrl,
  privacyPolicyUrl,
}: SupportRequestFormProps) => {
  const [apiError, setApiError] = useState(false);
  const topBoxRef = useRef<HTMLDivElement | null>(null);
  const errorAlertsRef = useRef<ErrorAlertsRef>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const router = useRouter();
  const contactPreferenceRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitted, isSubmitting },
    setFocus,
    watch,
    setValue,
  } = useForm<SupportRequestFormData>({
    resolver: zodResolver(supportRequestFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      accountHolder: AccountHolder.YES,
      requestFor: RequestFor.ME,
      nominate: Nominate.NO,
    },
  });

  const onSubmit = async (data: SupportRequestFormData) => {
    try {
      if (!isValid) return;

      let recaptchaToken = '';
      try {
        recaptchaRef.current?.reset();
        recaptchaToken = (await recaptchaRef.current?.executeAsync()) || '';
        if (!recaptchaToken) {
          throw new Error('Failed to get recaptcha token');
        }
      } catch (err) {
        console.error('Recaptcha error:', err);
        setApiError(true);
        return;
      }

      setApiError(false);
      await sendSupportRequest(data, recaptchaToken);
      if (successUrl) {
        router.push(successUrl);
      }
    } catch (error) {
      setApiError(true);
      topBoxRef.current?.scrollIntoView({ behavior: 'smooth' });
      console.error('Submission error:', error);
    }
  };

  const contactPreference = watch('contactPreference');

  const handleSetFocus = (name: string) => {
    if (name === 'contactPreference') {
      setFocus('contactPreference.0');
    } else {
      setFocus(name as any);
    }
  };

  return (
    <Box
      ref={topBoxRef}
      data-testid='support-request-form'
      border={false}
      className={styles['support-request-form']}
    >
      {apiError && (
        <Box margin='lg 0' border={false}>
          <Text color='error'>{errorMessage}</Text>
        </Box>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        {!isValid && isSubmitted && errors && (
          <Box margin='lg 0' border={false}>
            <ErrorAlert
              ref={errorAlertsRef}
              errors={errors}
              setFocus={handleSetFocus}
            />
          </Box>
        )}

        <Box gap='lg' border={false}>
          <Box gap='lg' border={false}>
            <Text size='T500' weight='bold' as='h2'>
              About you
            </Text>
            <Text>Please complete all fields unless stated otherwise.</Text>
            <Text>Are you the account holder?</Text>
            <FormRadioGroup
              id='accountHolder'
              error={errors?.accountHolder?.message}
            >
              <Box gap='lg' border={false} padding='sm'>
                <FormRadioInput
                  isFlag
                  label={"Yes, I'm the account holder"}
                  value='yes'
                  {...register('accountHolder')}
                />
                <FormRadioInput
                  isFlag
                  label="No, I'm not the account holder"
                  value='no'
                  {...register('accountHolder')}
                />
              </Box>
            </FormRadioGroup>
            <Box border={false} gap='lg'>
              <FormInput
                id='firstName'
                label={<Text>First name</Text>}
                {...register('firstName')}
                error={errors?.firstName}
                placeholder='e.g. John'
              />
              <FormInput
                id='lastName'
                label={<Text>Last name</Text>}
                {...register('lastName')}
                error={errors?.lastName}
                placeholder='e.g. Smith'
              />
              <FormInput
                id='mobileNumber'
                label={<Text>O2 mobile number</Text>}
                {...register('mobileNumber')}
                error={errors?.mobileNumber}
                placeholder='e.g. 07543654437'
              />
              <FormInput
                id='email'
                label={<Text>Email address</Text>}
                {...register('email')}
                error={errors?.email}
                placeholder='e.g. j.smith@email.com'
              />
            </Box>
            <Text>Who is this request for?</Text>
            <FormRadioGroup id='requestFor' error={errors?.requestFor?.message}>
              <Box gap='lg' border={false} padding='sm'>
                <FormRadioInput
                  isFlag
                  label={"It's for me"}
                  value='me'
                  {...register('requestFor')}
                  onChange={() => {
                    setValue('requestFor', RequestFor.ME);
                    setValue('lpaRegister', undefined);
                    setValue('lpaCode', undefined);
                  }}
                />
                <FormRadioInput
                  isFlag
                  label='Someone else whose account I help manage'
                  value='another'
                  {...register('requestFor')}
                  onChange={() => {
                    setValue('requestFor', RequestFor.ANOTHER);
                    setValue('lpaRegister', LpaRegister.YES);
                    setValue('lpaCode', undefined);
                  }}
                />
                {watch('requestFor') === RequestFor.ANOTHER && (
                  <Box border={false} gap='lg'>
                    <Text>
                      Do you want to register a Lasting Power of Attorney on the
                      account?
                    </Text>
                    <FormRadioGroup
                      id='lpaRegister'
                      error={errors?.lpaRegister?.message}
                    >
                      <Box gap='lg' border={false} padding='sm'>
                        <FormRadioInput
                          isFlag
                          label='Yes'
                          value='yes'
                          {...register('lpaRegister')}
                          onChange={() => {
                            setValue('lpaRegister', LpaRegister.YES);
                          }}
                        />
                        <FormRadioInput
                          isFlag
                          label='No'
                          value='no'
                          {...register('lpaRegister')}
                          onChange={() => {
                            setValue('lpaRegister', LpaRegister.NO);
                            setValue('lpaCode', undefined);
                          }}
                        />
                      </Box>
                    </FormRadioGroup>
                    {watch('lpaRegister') === LpaRegister.YES && (
                      <Box border={false} gap='lg'>
                        <FormInput
                          dataTestId='lpaCode'
                          id='lpaCode'
                          label={<Text>Please input the LPA code:</Text>}
                          {...register('lpaCode')}
                          placeholder='0000-0000-0000'
                          error={errors?.lpaCode}
                        />
                        <Text>
                          For other legal requests or support, please add
                          details in the &apos;Support needs&apos; box below.
                        </Text>
                      </Box>
                    )}
                    {watch('lpaRegister') === LpaRegister.NO && (
                      <Text>
                        For other legal requests or support, please add details
                        in the &apos;Support needs&apos; box below.
                      </Text>
                    )}
                  </Box>
                )}
              </Box>
            </FormRadioGroup>
          </Box>

          <Box gap='lg' border={false}>
            <Text size='T500' weight='bold' as='h2'>
              Contact preference
            </Text>
            <Text>
              We&apos;ll try to get in touch using your preferred contact
              method, but sometimes you might get an automatically generated
              text or email. Please select which method of contact you prefer.
              We may need to contact you about the information you share on this
              form. This could come from an unknown number or email address.
            </Text>
            <FormCheckboxGroup
              id='contactPreference'
              error={errors?.contactPreference?.root?.message}
              ref={contactPreferenceRef}
            >
              <Box gap='lg' border={false} padding='sm'>
                {contactPreferenceInputList.map((item, index) => {
                  return (
                    <FormCheckboxInput
                      label={item.label}
                      key={item.name}
                      value={item.name}
                      isSquare
                      isFlag
                      {...register(`contactPreference.${index}`)}
                      error={!!errors?.contactPreference}
                    />
                  );
                })}
              </Box>
              {contactPreference?.includes(ContactOptions.OTHER) && (
                <Box border={false}>
                  <FormInput
                    id='contactPreferenceOtherDetail'
                    label={<Text>Other contact preference</Text>}
                    placeholder='Type here'
                    {...register('contactPreferenceOtherDetail')}
                    error={errors?.contactPreferenceOtherDetail}
                  />
                </Box>
              )}
            </FormCheckboxGroup>
          </Box>

          <Box gap='lg' border={false}>
            <Text size='T500' weight='bold' as='h2'>
              Your account
            </Text>
            <Text>
              Would you like to nominate a trusted friend or family member to
              help you manage your account?
            </Text>
            <FormRadioGroup id='nominate' error={errors?.nominate?.message}>
              <Box gap='lg' border={false} padding='sm'>
                <FormRadioInput
                  isFlag
                  label={'Yes, I would like to nominate'}
                  value='yes'
                  {...register('nominate')}
                  onChange={() => setValue('nominate', Nominate.YES)}
                />
                <FormRadioInput
                  isFlag
                  label="No, I don't want to nominate"
                  value='no'
                  {...register('nominate')}
                  onChange={() => {
                    setValue('nominate', Nominate.NO);
                    setValue('nominee', undefined);
                  }}
                />
              </Box>
            </FormRadioGroup>

            {watch('nominate') === Nominate.YES && (
              <Box border={false}>
                <FormInput
                  id='nominee'
                  label={
                    <Text>
                      Please provide the name of who you would like to nominate?
                    </Text>
                  }
                  {...register('nominee')}
                  error={errors?.nominee}
                  placeholder='e.g. Jane Smith'
                />
              </Box>
            )}
          </Box>

          <Box gap='lg' border={false}>
            <Text size='T500' weight='bold' as='h2'>
              Support needs
            </Text>
            <Text>
              Please tell us about your disability or personal circumstances,
              and the adjustments you need us to make for you. This information
              will be recorded on your account for future reference. The
              information you provide will help us assess your requirements and
              any reasonable adjustments to how we work and communicate with
              you.
            </Text>
            <Box border={false}>
              <FormTextarea
                dataTestId='support'
                id='support'
                {...register('support')}
                placeholder='Type here'
                error={errors?.support}
              />
            </Box>
          </Box>

          <Box gap='lg' border={false}>
            <Box border={false} padding='sm'>
              <FormCheckboxInput
                label='I consent to you, Telefonica UK Limited, using my information
                to assess my needs and adjust the services you provide to me.'
                isSquare
                isFlag
                {...register('consent')}
                error={!!errors?.consent}
              />
            </Box>
            <Text>
              You can withdraw your consent or update your details at any time
              by emailing{' '}
              <Link
                href={`mailto:${ACCESS_FOR_YOU_EMAIL}`}
                prefetch={false}
                target='_blank'
              >
                {ACCESS_FOR_YOU_EMAIL}. Find out more about how we use your data
                in our{' '}
              </Link>
              {privacyPolicyUrl && (
                <Link href={privacyPolicyUrl} prefetch={false} target='_blank'>
                  Privacy Policy
                </Link>
              )}
              .
            </Text>
          </Box>

          <Button type='submit' disabled={isSubmitting}>
            Submit form
          </Button>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={recaptchaSiteKey}
            size='invisible'
            badge='inline'
          />
        </Box>
      </form>
    </Box>
  );
};
